import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
// Type-only: app/store.ts imports this reducer, so a value import here would
// close an import cycle at runtime.
import type { RootState } from "../../../app/store";
import { hubspotService } from "../../../services/hubspotService";
import {
  HubspotBulkJob,
  HubspotBulkJobCreate,
  HubspotBulkJobsParams,
  HubspotConnectResponse,
  HubspotConnection,
  HubspotExecution,
  HubspotExecutionsParams,
  HubspotList,
  HubspotPropertySyncResponse,
} from "../types";

export interface HubspotState {
  connection: HubspotConnection | null;
  executions: HubspotExecution[];
  // Separate from `loading`, which four thunks share: the card needs to know
  // "have we asked yet", not "is any request in flight". Without it a null
  // connection on first paint is indistinguishable from a disconnected portal,
  // and the screen flashes "Connect to HubSpot" before settling on "Connected".
  connectionLoaded: boolean;
  loading: boolean;
  error: string | null;
  connecting: boolean;
  disconnecting: boolean;
  lists: HubspotList[];
  // Separate from `loading` for the same reason connectionLoaded is: the picker
  // must tell "not fetched yet" from "this portal genuinely has no lists", and
  // only the second one should render the empty-state copy.
  listsLoaded: boolean;
  listsLoading: boolean;
  bulkJobs: HubspotBulkJob[];
  creatingBulkJob: boolean;
}

const initialState: HubspotState = {
  connection: null,
  executions: [],
  connectionLoaded: false,
  loading: false,
  error: null,
  connecting: false,
  disconnecting: false,
  lists: [],
  listsLoaded: false,
  listsLoading: false,
  bulkJobs: [],
  creatingBulkJob: false,
};

// FastAPI answers a validation failure with a list under `detail`, so the
// string check matters - without it a banner renders "[object Object]".
export const extractErrorMessage = (error: unknown, fallback: string): string => {
  const err = error as {
    response?: { data?: { detail?: unknown; message?: unknown } };
    message?: string;
  };
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) {
    return detail;
  }
  const message = err?.response?.data?.message;
  if (typeof message === "string" && message) {
    return message;
  }
  return err?.message || fallback;
};

export const startHubspotConnect = createAsyncThunk<
  HubspotConnectResponse,
  void,
  { rejectValue: string }
>("hubspot/startConnect", async (_arg, { rejectWithValue }) => {
  try {
    return await hubspotService.startConnect();
  } catch (error) {
    return rejectWithValue(
      extractErrorMessage(error, "Could not start the HubSpot connection")
    );
  }
});

export const fetchHubspotConnection = createAsyncThunk<
  HubspotConnection | null,
  void,
  { rejectValue: string }
>("hubspot/fetchConnection", async (_arg, { rejectWithValue }) => {
  try {
    return await hubspotService.getConnection();
  } catch (error) {
    return rejectWithValue(
      extractErrorMessage(error, "Could not load the HubSpot connection")
    );
  }
});

export const disconnectHubspot = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>("hubspot/disconnect", async (_arg, { rejectWithValue }) => {
  try {
    await hubspotService.disconnect();
  } catch (error) {
    return rejectWithValue(
      extractErrorMessage(error, "Could not disconnect the HubSpot portal")
    );
  }
});

export const fetchHubspotExecutions = createAsyncThunk<
  HubspotExecution[],
  HubspotExecutionsParams | undefined,
  { rejectValue: string }
>("hubspot/fetchExecutions", async (params, { rejectWithValue }) => {
  try {
    return await hubspotService.getExecutions(params ?? {});
  } catch (error) {
    return rejectWithValue(
      extractErrorMessage(error, "Could not load the HubSpot activity")
    );
  }
});

export const syncHubspotProperties = createAsyncThunk<
  HubspotPropertySyncResponse,
  void,
  { rejectValue: string }
>("hubspot/syncProperties", async (_arg, { rejectWithValue }) => {
  try {
    return await hubspotService.syncProperties();
  } catch (error) {
    return rejectWithValue(
      extractErrorMessage(error, "Could not sync the ReachStream properties")
    );
  }
});

export const fetchHubspotLists = createAsyncThunk<
  HubspotList[],
  string | void,
  { rejectValue: string }
>("hubspot/fetchLists", async (search, { rejectWithValue }) => {
  try {
    return await hubspotService.getLists(typeof search === "string" ? search : "");
  } catch (error) {
    return rejectWithValue(
      extractErrorMessage(error, "Could not load your HubSpot lists")
    );
  }
});

export const createHubspotBulkJob = createAsyncThunk<
  HubspotBulkJob,
  HubspotBulkJobCreate,
  { rejectValue: string }
>("hubspot/createBulkJob", async (payload, { rejectWithValue }) => {
  try {
    return await hubspotService.createBulkJob(payload);
  } catch (error) {
    return rejectWithValue(
      extractErrorMessage(error, "Could not start the list verification")
    );
  }
});

export const fetchHubspotBulkJobs = createAsyncThunk<
  HubspotBulkJob[],
  HubspotBulkJobsParams | undefined,
  { rejectValue: string }
>("hubspot/fetchBulkJobs", async (params, { rejectWithValue }) => {
  try {
    return await hubspotService.getBulkJobs(params ?? {});
  } catch (error) {
    return rejectWithValue(
      extractErrorMessage(error, "Could not load your list verifications")
    );
  }
});

// Polling while a job is in flight refreshes only the newest jobs and merges
// them into what is loaded, so each poll costs one page rather than every job
// the table can page through. A live job is always among the newest.
export const pollHubspotBulkJobs = createAsyncThunk<
  HubspotBulkJob[],
  HubspotBulkJobsParams | undefined,
  { rejectValue: string }
>("hubspot/pollBulkJobs", async (params, { rejectWithValue }) => {
  try {
    return await hubspotService.getBulkJobs(params ?? {});
  } catch (error) {
    return rejectWithValue(
      extractErrorMessage(error, "Could not load your list verifications")
    );
  }
});

const hubspotSlice = createSlice({
  name: "hubspot",
  initialState,
  reducers: {
    clearHubspotError: (state) => {
      state.error = null;
    },
    resetHubspotState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(startHubspotConnect.pending, (state) => {
        state.connecting = true;
        state.error = null;
      })
      .addCase(startHubspotConnect.fulfilled, (state) => {
        state.connecting = false;
      })
      .addCase(startHubspotConnect.rejected, (state, action) => {
        state.connecting = false;
        state.error = action.payload || "Could not start the HubSpot connection";
      })

      .addCase(fetchHubspotConnection.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHubspotConnection.fulfilled, (state, action) => {
        state.loading = false;
        state.connectionLoaded = true;
        state.connection = action.payload;
      })
      .addCase(fetchHubspotConnection.rejected, (state, action) => {
        state.loading = false;
        // Set on failure too. A request that errored is still an answered
        // question, and leaving this false would hold the loader forever.
        state.connectionLoaded = true;
        state.error = action.payload || "Could not load the HubSpot connection";
      })

      .addCase(disconnectHubspot.pending, (state) => {
        state.disconnecting = true;
        state.error = null;
      })
      .addCase(disconnectHubspot.fulfilled, (state) => {
        state.disconnecting = false;
        state.connection = null;
        // These all belong to the connection that just went away.
        state.executions = [];
        state.lists = [];
        state.listsLoaded = false;
        state.bulkJobs = [];
      })
      .addCase(disconnectHubspot.rejected, (state, action) => {
        state.disconnecting = false;
        state.error = action.payload || "Could not disconnect the HubSpot portal";
      })

      .addCase(fetchHubspotExecutions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHubspotExecutions.fulfilled, (state, action) => {
        state.loading = false;
        state.executions = action.payload;
      })
      .addCase(fetchHubspotExecutions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Could not load the HubSpot activity";
      })

      .addCase(syncHubspotProperties.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(syncHubspotProperties.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(syncHubspotProperties.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Could not sync the ReachStream properties";
      })

      .addCase(fetchHubspotLists.pending, (state) => {
        state.listsLoading = true;
        state.error = null;
      })
      .addCase(fetchHubspotLists.fulfilled, (state, action) => {
        state.listsLoading = false;
        state.listsLoaded = true;
        state.lists = action.payload;
      })
      .addCase(fetchHubspotLists.rejected, (state, action) => {
        state.listsLoading = false;
        // Set on failure too: a request that errored is still an answered
        // question, and leaving this false holds the picker's loader forever.
        state.listsLoaded = true;
        state.error = action.payload || "Could not load your HubSpot lists";
      })

      .addCase(createHubspotBulkJob.pending, (state) => {
        state.creatingBulkJob = true;
        state.error = null;
      })
      .addCase(createHubspotBulkJob.fulfilled, (state, action) => {
        state.creatingBulkJob = false;
        // Prepended rather than refetched: the jobs table is newest-first and
        // the new row is already the newest.
        state.bulkJobs = [action.payload, ...state.bulkJobs];
      })
      .addCase(createHubspotBulkJob.rejected, (state, action) => {
        state.creatingBulkJob = false;
        state.error = action.payload || "Could not start the list verification";
      })

      .addCase(fetchHubspotBulkJobs.fulfilled, (state, action) => {
        state.bulkJobs = action.payload;
      })
      .addCase(fetchHubspotBulkJobs.rejected, (state, action) => {
        state.error = action.payload || "Could not load your list verifications";
      })

      .addCase(pollHubspotBulkJobs.fulfilled, (state, action) => {
        const fresh = new Map(action.payload.map((job) => [job.id, job]));
        const known = new Set(state.bulkJobs.map((job) => job.id));
        // Unseen jobs are newer than everything loaded (e.g. started from
        // another tab), so they go on top, still newest-first.
        state.bulkJobs = [
          ...action.payload.filter((job) => !known.has(job.id)),
          ...state.bulkJobs.map((job) => fresh.get(job.id) ?? job),
        ];
      })
      .addCase(pollHubspotBulkJobs.rejected, (state, action) => {
        state.error = action.payload || "Could not load your list verifications";
      });
  },
});

export const { clearHubspotError, resetHubspotState } = hubspotSlice.actions;

export const selectHubspotConnection = (state: RootState) => state.hubspot.connection;
export const selectHubspotExecutions = (state: RootState) => state.hubspot.executions;
export const selectHubspotLoading = (state: RootState) => state.hubspot.loading;
export const selectHubspotConnectionLoaded = (state: RootState) =>
  state.hubspot.connectionLoaded;
export const selectHubspotError = (state: RootState) => state.hubspot.error;
export const selectHubspotConnecting = (state: RootState) => state.hubspot.connecting;
export const selectHubspotDisconnecting = (state: RootState) => state.hubspot.disconnecting;
export const selectIsHubspotConnected = (state: RootState) =>
  state.hubspot.connection?.status === "active";
export const selectHubspotLists = (state: RootState) => state.hubspot.lists;
export const selectHubspotListsLoading = (state: RootState) =>
  state.hubspot.listsLoading;
export const selectHubspotListsLoaded = (state: RootState) =>
  state.hubspot.listsLoaded;
export const selectHubspotBulkJobs = (state: RootState) => state.hubspot.bulkJobs;
export const selectHubspotCreatingBulkJob = (state: RootState) =>
  state.hubspot.creatingBulkJob;
// Connected but without the read scopes means the portal was installed before
// bulk list verification shipped: the workflow action still runs, but lists
// cannot be read until the customer reconnects.
export const selectHubspotCanVerifyLists = (state: RootState) =>
  state.hubspot.connection?.can_verify_lists === true;

export default hubspotSlice.reducer;
